// Sidebar navigation: the primary/secondary role merge, plus the mnemonic
// letter each destination owns.
//
// Extracted from Layout so the merge has one implementation and the mnemonic
// registry sees exactly the destinations the sidebar renders — a user can never
// be shown a mnemonic for a route their role cannot reach.

/**
 * Letters are assigned per destination and are unique within every single
 * role's menu. A user holding two roles can produce a duplicate; the registry resolves
 * to the primary role's entry and the loser simply renders no underline, so
 * nothing on screen ever claims a key it doesn't own.
 */
export const NAV_MNEMONICS = {
  '/quotations': 'q',
  '/proforma-invoices': 'f',
  '/orders': 'o',
  '/job-cards': 'j',
  '/plates': 'l',
  '/dies': 'd',
  '/inventory': 'i',
  '/plate-register': 'p',
  '/die-register': 'g',
  '/customers': 'u',
  '/dispatch': 'h',
  '/reports': 'r',
  '/users': 'e',
  '/recycle-bin': 'z',
  '/machines': 'm',
  '/admin-settings': 's',
};

export const navMnemonic = (path) => NAV_MNEMONICS[path];

/**
 * Which lane of the business each destination belongs to.
 *
 * A flat list is one you read rather than a menu you aim at. The lanes are the
 * order work actually moves in — take it, make it, ship it — with the reference
 * data and the setup screens after, because those are looked up rather than
 * worked through. Reports rides with Dispatch: what it answers — what is late,
 * what has gone out against the PO — is read once the goods are moving.
 */
export const NAV_SECTIONS = [
  // the priced documents ride with Work: a quotation is what an order starts as
  { id: 'work', label: 'Work', paths: [
    '/orders', '/quotations', '/proforma-invoices', '/job-cards', '/plates', '/dies',
  ] },
  { id: 'out', label: 'Out the door', paths: ['/dispatch', '/reports'] },
  { id: 'directory', label: 'Directory', paths: [
    '/customers', '/inventory', '/plate-register', '/die-register',
  ] },
  { id: 'setup', label: 'Setup', paths: [
    '/users', '/machines', '/admin-settings', '/network-access', '/recycle-bin',
  ] },
];

const SECTION_OF = NAV_SECTIONS.reduce((map, section) => {
  for (const path of section.paths) map[path] = section.id;
  return map;
}, {});

/**
 * Collect a flat nav list into its lanes.
 *
 * Bucketed by lane, not by adjacency — a role's menu lists Orders before
 * Customers before Users, and grouping as you walk that order produces
 * Work / Directory / Setup / Directory / Work, which is worse than no grouping
 * at all. Lanes come out in NAV_SECTIONS order; items keep their order within a
 * lane; anything with no lane collects under "More" at the end.
 *
 * The secondary-role separator splits the list first, so a secondary role's
 * destinations stay grouped behind their own divider rather than merging into
 * the primary role's lanes.
 */
function bucketIntoLanes(items) {
  const byLane = new Map();
  for (const item of items) {
    const laneId = SECTION_OF[item.path] || 'more';
    if (!byLane.has(laneId)) byLane.set(laneId, []);
    byLane.get(laneId).push(item);
  }

  const lanes = [];
  for (const section of NAV_SECTIONS) {
    const laneItems = byLane.get(section.id);
    if (laneItems?.length) lanes.push({ id: section.id, label: section.label, items: laneItems });
  }
  const rest = byLane.get('more');
  if (rest?.length) lanes.push({ id: 'more', label: 'More', items: rest });
  return lanes;
}

export function groupNavItems(items) {
  const splitAt = items.findIndex(item => item.separator);
  if (splitAt === -1) return bucketIntoLanes(items);

  return [
    ...bucketIntoLanes(items.slice(0, splitAt)),
    { id: 'separator', separator: true, items: [] },
    ...bucketIntoLanes(items.slice(splitAt + 1)),
  ];
}

/**
 * Primary-role items first, then any destinations the user's secondary roles
 * add, deduplicated by path and separated by a divider.
 */
export function buildNavItems(user, roleNav) {
  if (!user) return roleNav.receptionist || [];

  const allRoles = user.roles || [user.role];
  const primaryItems = roleNav[user.role] || [];
  const seenPaths = new Set(primaryItems.map((item) => item.path));

  const secondaryItems = [];
  for (const role of allRoles.filter((r) => r !== user.role)) {
    for (const item of roleNav[role] || []) {
      if (!seenPaths.has(item.path)) {
        seenPaths.add(item.path);
        secondaryItems.push(item);
      }
    }
  }

  if (secondaryItems.length > 0) {
    return [...primaryItems, { separator: true }, ...secondaryItems];
  }
  return primaryItems;
}
